/**
 * contactLines (34i87, 2026-10-21) — the ONE pure answer to "who do we call on
 * this account, and from where?" Shared by the header's circles, the rail's
 * People block, the People & Lines drawer, Book a Call's Confirm step and the
 * owner-transfer snapshot. No React, no queries — plain functions over the
 * rows the callers already hold.
 *
 * Sources, in order of trust:
 *   · Pegasus contacts (`contact` table — the office typed them, fully editable)
 *   · ESC's Address-Information grid for the property (six named slots:
 *     Contact · Phone · Ext · Email · JobTitle) — the FRESH labeling the office
 *     maintains, when the loccontacts backfill has landed for the location
 *   · else the labeled account phone bank (customer phone1..4 + lbl_phone1..4)
 *     and the property's email slots
 *
 * NEW-OWNER-CONTACTS (Seth 2026-10-21: "if I add them as the new homeowner and
 * just fill in the name … I don't want the old home's contacts now associated
 * with the record"): once a SALE is recorded in Pegasus for the property,
 * everything ESC knew about who to call is the PREVIOUS owner's — the grid, the
 * phone bank, the property emails, and any Pegasus contact added before the
 * handoff. They stop being "people on this account" and live under Previous
 * Owners. The current owner's own lines (the identity recorded at the handoff)
 * take their place. `contactEraOf` decides whether that rule is in force;
 * `buildLines` applies it; `splitLinesByEra` keeps the previous owner's lines
 * reachable for the Previous Owners view.
 */
import type { CustomerWithLocations, LocationRow } from "./api";
import type { ContactRow } from "./CustomerContacts";

const p10 = (s: string) => s.replace(/\D/g, "").slice(-10);

// ----------------------------------------------------------------------------
// ESC's Address-Information grid (MIR-LOCLBL, Seth 2026-06-10 with screenshot):
// six named slots per location, each Contact + Phone + Ext + Email + JobTitle
// ("Cell - Truc | 612-226-8099 | tttruong67@…"). This is the FRESH labeling the
// office actually maintains (the customer-level lbl_phone1..4 proved stale:
// ESC's grid says 507-202-6622 = Maintenance Rep, the customer label said
// Owner). Empty slots skipped; null columns (backfill not yet run) → empty grid
// and the caller falls back to buildPhoneLines.
// ----------------------------------------------------------------------------

export interface GridRow {
  name: string | null;
  title: string | null;
  phone: string | null;
  ext: string | null;
  email: string | null;
}

/**
 * ESC's grid "Contact" slot is a LABEL far more often than a person — "Home"
 * (75k rows), "Cellular", "Cell", "Work", "Office" — and when a person is
 * named it reads "Cell - John" or "Missy - Verde". Split it so a person is a
 * name and a label is a role: the People card reads "John · Cell", the
 * confirmation text greets "Hi John", never "Hi Cell".
 */
const LABEL_RE = /^(home|house|cell|cellular|mobile|work|office|phone|fax|main|shop|landline|land line|home phone|cell phone|work phone|wife|husband|spouse|owner|tenant|renter|billing|ap|accounts payable|emergency|other|alt|alternate|primary|secondary|day|evening|night)$/i;
// "Wife Corky", "Son Mike", "Tenant Dana" — a relation in front of a first name.
const RELATION_RE = /^(wife|husband|spouse|partner|son|daughter|mother|father|mom|dad|brother|sister|tenant|renter|owner|landlord|realtor|caretaker|neighbor|manager|pm)\s+([A-Za-z][A-Za-z'.-]*(?:\s+[A-Za-z][A-Za-z'.-]*)?)$/i;
export function splitGridContact(raw: string | null | undefined): { name: string | null; label: string | null } {
  const v = (raw ?? "").trim();
  if (!v) return { name: null, label: null };
  if (LABEL_RE.test(v)) return { name: null, label: v };
  const cap = (x: string) => x[0].toUpperCase() + x.slice(1).toLowerCase();
  // "Wife Corky" → Corky · Wife (also the name half of "Cell - Wife Corky").
  const relation = (name: string, label: string | null): { name: string; label: string | null } => {
    const r = name.match(RELATION_RE);
    if (!r || LABEL_RE.test(r[2])) return { name, label };
    return { name: r[2], label: [cap(r[1]), label].filter(Boolean).join(" · ") };
  };
  const m = v.match(/^(.+?)\s*[-–/]\s*(.+)$/);
  if (m) {
    const [a, b] = [m[1].trim(), m[2].trim()];
    if (LABEL_RE.test(a)) return relation(b, a);
    if (LABEL_RE.test(b)) return relation(a, b);
    return { name: a, label: b };
  }
  return relation(v, null);
}

export function buildContactGrid(loc: LocationRow): GridRow[] {
  const slots: [string | null, string | null, string | null, string | null, string | null][] = [
    [loc.contact1, loc.phone1, loc.ext1, loc.email, loc.jobtitle1],
    [loc.contact2, loc.phone2, loc.ext2, loc.email2, loc.jobtitle2],
    [loc.contact3, loc.phone3, loc.ext3, loc.email3, loc.jobtitle3],
    [loc.contact4, loc.phone4, loc.ext4, loc.email4, loc.jobtitle4],
    [loc.contact5, loc.phone5, loc.ext5, loc.email5, loc.jobtitle5],
    [loc.contact6, loc.phone6, loc.ext6, loc.email6, loc.jobtitle6],
  ];
  return slots
    .map(([contact, phone, ext, email, title]) => {
      const { name, label } = splitGridContact(contact);
      return {
        name,
        phone: phone?.trim() || null,
        ext: ext?.trim() || null,
        email: email?.trim() || null,
        // The job title when ESC has one, else the slot's label ("Cell", "Home");
        // a relation in the slot and a title both → "Wife · Cell".
        title: [label, title?.trim() || null].filter((x, i, arr) => x && arr.indexOf(x) === i).join(" · ") || null,
      };
    })
    // A row earns space only when it can be USED — a bare label with no
    // phone and no email ("Office", "Fax") is noise (Seth 2026-06-11 r3).
    .filter((s) => s.phone || s.email);
}

/** True once the loccontacts backfill has landed for this location. */
export function hasContactGrid(loc: LocationRow): boolean {
  return [loc.contact1, loc.contact2, loc.contact3, loc.contact4, loc.contact5, loc.contact6].some(
    (c) => !!c?.trim(),
  );
}

// ----------------------------------------------------------------------------
// The labeled phone bank (Seth 2026-06-10). Location lines lead (per-location
// numbers are the operational truth — BUILD-13b A1), but each line picks up
// ESC's customer-level label (lbl_phone1..4: Owner / Home / Work / Cellular…)
// when the digits match; labeled customer-only lines follow so context like
// "Owner · 507-202-6622" is never lost. Deduped on last-10. Fallback for
// locations whose contact GRID hasn't been backfilled yet.
// ----------------------------------------------------------------------------

export function buildPhoneLines(
  customer: {
    phone1: string | null; phone2: string | null; phone3: string | null; phone4: string | null;
    lbl_phone1: string | null; lbl_phone2: string | null; lbl_phone3: string | null; lbl_phone4: string | null;
  },
  loc: { phone1: string | null; phone2: string | null; phone3: string | null },
): { phone: string; label: string | null }[] {
  const custPairs: [string | null, string | null][] = [
    [customer.phone1, customer.lbl_phone1],
    [customer.phone2, customer.lbl_phone2],
    [customer.phone3, customer.lbl_phone3],
    [customer.phone4, customer.lbl_phone4],
  ];
  const labelFor = (p: string): string | null => {
    const hit = custPairs.find(([cp]) => cp && p10(cp) === p10(p));
    return hit?.[1]?.trim() || null;
  };
  const out: { phone: string; label: string | null }[] = [];
  const seen = new Set<string>();
  for (const p of [loc.phone1, loc.phone2, loc.phone3]) {
    if (!p || seen.has(p10(p))) continue;
    seen.add(p10(p));
    out.push({ phone: p, label: labelFor(p) });
  }
  for (const [p, lbl] of custPairs) {
    if (!p || seen.has(p10(p))) continue;
    seen.add(p10(p));
    out.push({ phone: p, label: lbl?.trim() || null });
  }
  return out;
}

// ----------------------------------------------------------------------------
// Lines — one row per person-or-number, from every source, de-duplicated.
// ----------------------------------------------------------------------------

export interface Line {
  key: string;
  /** pegasus = a `contact` row · pegasus-account = a Pegasus-born customer's own row ·
   *  pegasus-owner = the owner recorded at a handoff · esc-* = ESC's copy */
  origin: "pegasus" | "pegasus-account" | "pegasus-owner" | "esc-property" | "esc-account";
  name: string | null;
  role: string | null;
  phone: string | null;
  ext: string | null;
  email: string | null;
  contact: ContactRow | null;
  /** The phone's label when the line carries one apart from its role (Cell · Home · Work). */
  label?: string | null;
  /** CONTACT-ONE-TABLE: the ESC line key this Pegasus line replaced (the ESC copy is hidden). */
  replaces?: string | null;
  /** Switched off in the suite — kept for the editor's "switched off" list, never dialed. */
  hidden?: boolean;
  mayAuthorize?: boolean;
  /** REACH-DEFAULTS-01: booking defaults carried by the line. */
  reach?: { confirm: boolean; headsup: "call" | "text" | null; email: boolean };
}

/** The stable key of an ESC line — by its value, so a re-sync that changes the number releases the correction. */
export const phoneKey = (phone: string) => `phone:${p10(phone)}`;
export const emailKey = (email: string) => `email:${email.trim().toLowerCase()}`;
/** Every key an ESC line answers to (a grid row can carry a phone AND an email). */
export function lineKeys(l: { phone: string | null; email: string | null }): string[] {
  const out: string[] = [];
  if (l.phone) out.push(phoneKey(l.phone));
  if (l.email) out.push(emailKey(l.email));
  return out;
}

export interface ContactEra {
  /** The day the current owner's tenure began — set only for a Pegasus-recorded sale / move. */
  handoffISO: string;
  owner: { name: string; phone1: string | null; phone2: string | null; email1: string | null; email2: string | null } | null;
}

/** The era from a location's ownership answer: a recorded sale/move with a Pegasus-native owner. */
export function contactEraOf(
  owned:
    | {
        ownership: { role: string; start_date: string | null; end_date: string | null; source: string | null; owner_id: string | null }[];
        currentOwner: { name: string; phone1: string | null; phone2: string | null; email1: string | null; email2: string | null; source: string } | null;
      }
    | null
    | undefined,
): ContactEra | null {
  if (!owned?.currentOwner || owned.currentOwner.source !== "pegasus") return null;
  const open = owned.ownership.find((r) => !r.end_date && r.role === "owner" && (r.source === "sale" || r.source === "move"));
  if (!open?.start_date) return null;
  return { handoffISO: open.start_date, owner: owned.currentOwner };
}

/**
 * Every person and line on the account from every source, de-duplicated:
 * Pegasus contacts first (editable), then ESC's property contact grid, else
 * the labeled account phone bank + the property emails. With an `era`, the
 * previous owner's lines are left out (splitLinesByEra gets them back for the
 * Previous Owners view).
 */
export function buildLines(customer: CustomerWithLocations, location: LocationRow | null, contacts: ContactRow[], era?: ContactEra | null): Line[] {
  return era ? splitLinesByEra(customer, location, contacts, era).current : buildAllLines(customer, location, contacts);
}

export function splitLinesByEra(customer: CustomerWithLocations, location: LocationRow | null, contacts: ContactRow[], era: ContactEra): { current: Line[]; previous: Line[] } {
  const all = buildAllLines(customer, location, contacts);
  const day = era.handoffISO.slice(0, 10);
  const isCurrent = (l: Line) =>
    l.origin === "pegasus-account" ||
    (l.origin === "pegasus" && !!l.contact && (l.contact.created_at ?? "").slice(0, 10) >= day);
  const current: Line[] = [];
  const previous: Line[] = [];
  for (const l of all) (isCurrent(l) ? current : previous).push(l);
  // The current owner's own lines, deduped against Pegasus contacts added since.
  if (era.owner) {
    const seenP = new Set(current.map((l) => l.phone && p10(l.phone)).filter(Boolean) as string[]);
    const seenE = new Set(current.map((l) => l.email?.trim().toLowerCase()).filter(Boolean) as string[]);
    const o = era.owner;
    const pairs: [string | null, string | null][] = [[o.phone1, o.email1], [o.phone2, o.email2]];
    const ownerLines: Line[] = [];
    pairs.forEach(([ph, em], i) => {
      const phone = ph && !seenP.has(p10(ph)) ? ph : null;
      const email = em && !seenE.has(em.trim().toLowerCase()) ? em : null;
      if (!phone && !email) return;
      if (phone) seenP.add(p10(phone));
      if (email) seenE.add(email.trim().toLowerCase());
      ownerLines.push({ key: `o:${i}`, origin: "pegasus-owner", name: o.name, role: i === 0 ? "Owner" : null, phone, ext: null, email, contact: null });
    });
    current.unshift(...ownerLines);
  }
  return { current, previous };
}

export function buildAllLines(customer: CustomerWithLocations, location: LocationRow | null, contacts: ContactRow[]): Line[] {
  return buildLinesDetailed(customer, location, contacts).lines;
}

/** A `contact` row as a Line (the one place the row is read). */
export function lineOfContact(c: ContactRow): Line {
  // "Wife Corky" typed as a contact's name reads as Corky · Wife, like ESC's slots.
  const sp = splitGridContact(c.name);
  const name = sp.label && sp.name ? sp.name : c.name;
  const role = sp.label && sp.name ? [sp.label, c.role?.trim() || null].filter((x, i, arr) => x && arr.indexOf(x) === i).join(" · ") : c.role;
  return {
    key: `c:${c.id}`, origin: "pegasus", name, role, phone: c.phone, ext: null, email: c.email, contact: c,
    label: c.label ?? null, replaces: c.replaces ?? null, hidden: !!c.hidden_at, mayAuthorize: !!c.may_authorize,
    reach: { confirm: !!c.reach_confirm, headsup: c.reach_headsup ?? null, email: !!c.reach_email },
  };
}

/**
 * CONTACT-ONE-TABLE (34i97): the visible list, plus what is switched off.
 *   · a Pegasus line with `replaces` stands in for that ESC line (ESC's copy is dropped);
 *   · a hidden Pegasus line is not shown — and if it replaces an ESC line, that line
 *     stays hidden too (an ESC number the office switched off, no replacement);
 *   · a Pegasus-born customer's own row is `pegasus-account`, never "from ESC".
 */
export function buildLinesDetailed(customer: CustomerWithLocations, location: LocationRow | null, contacts: ContactRow[]): { lines: Line[]; hidden: Line[] } {
  const out: Line[] = [];
  const hidden: Line[] = [];
  const replaced = new Set<string>();
  const sorted = contacts.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || (a.created_at ?? "").localeCompare(b.created_at ?? ""));
  for (const c of sorted) {
    const l = lineOfContact(c);
    if (c.replaces) replaced.add(c.replaces);
    (l.hidden ? hidden : out).push(l);
  }
  const seenPhones = new Set(out.map((l) => l.phone && p10(l.phone)).filter(Boolean) as string[]);
  const seenEmails = new Set(out.map((l) => l.email?.trim().toLowerCase()).filter(Boolean) as string[]);
  const escOrigin = (o: "esc-property" | "esc-account"): Line["origin"] => (customer.native ? "pegasus-account" : o);
  const isReplaced = (l: { phone: string | null; email: string | null }) => lineKeys(l).some((k) => replaced.has(k));
  if (location && hasContactGrid(location)) {
    buildContactGrid(location).forEach((g, i) => {
      if (isReplaced(g)) return; // corrected or switched off in Pegasus
      const dupPhone = g.phone && seenPhones.has(p10(g.phone));
      const dupEmail = g.email && seenEmails.has(g.email.trim().toLowerCase());
      if (dupPhone && (dupEmail || !g.email)) return; // already a Pegasus contact
      out.push({ key: `g:${i}`, origin: escOrigin("esc-property"), name: g.name, role: g.title, phone: g.phone, ext: g.ext, email: g.email, contact: null });
      if (g.phone) seenPhones.add(p10(g.phone));
      if (g.email) seenEmails.add(g.email.trim().toLowerCase());
    });
  } else {
    // No grid (backfill pending) — or no location row in hand yet: the labeled
    // account phone bank still answers.
    buildPhoneLines(customer, location ?? { phone1: null, phone2: null, phone3: null }).forEach((p) => {
      if (replaced.has(phoneKey(p.phone)) || seenPhones.has(p10(p.phone))) return;
      seenPhones.add(p10(p.phone));
      out.push({ key: `p:${p10(p.phone)}`, origin: escOrigin("esc-account"), name: null, role: p.label, phone: p.phone, ext: null, email: null, contact: null });
    });
    for (const e of location ? [location.email, location.email2, location.email3, location.email4, location.email5, location.email6] : []) {
      const em = e?.trim(); if (!em || replaced.has(emailKey(em)) || seenEmails.has(em.toLowerCase())) continue;
      seenEmails.add(em.toLowerCase());
      out.push({ key: `e:${em.toLowerCase()}`, origin: escOrigin("esc-property"), name: null, role: "Email on file", phone: null, ext: null, email: em, contact: null });
    }
  }
  return { lines: out, hidden };
}

/**
 * C360-BOARD-01: the best phone + email for the header's one-tap circles — the
 * SAME resolution the facts line displays (office correction wins). With an
 * `era` (a recorded sale), the previous owner's ESC numbers are no longer
 * candidates — the new owner's lines are.
 */
export function bestContactFor(
  customer: CustomerWithLocations,
  loc: LocationRow | null,
  contacts: ContactRow[],
  era?: ContactEra | null,
): { phone: string | null; email: string | null } {
  // CONTACT-ONE-TABLE: the Pegasus lines lead the list, so a correction wins by position.
  const lines = buildLines(customer, loc, contacts, era);
  return {
    phone: lines.find((l) => l.phone)?.phone ?? null,
    email: lines.find((l) => l.email)?.email ?? (era ? null : loc?.email ?? null),
  };
}

/**
 * What to keep of the outgoing owner when a sale is recorded: their first two
 * phones and emails in the owner row's slots, and every line spelled out in
 * the notes ("Truc (Cell) 612-226-8099 · tttruong67@…") so the Previous
 * Owners view can show exactly who we used to call.
 */
export function snapshotOfLines(lines: Line[]): { phone1: string | null; phone2: string | null; email1: string | null; email2: string | null; notes: string | null } {
  const phones: string[] = [];
  const emails: string[] = [];
  const seenP = new Set<string>();
  const seenE = new Set<string>();
  for (const l of lines) {
    if (l.phone && !seenP.has(p10(l.phone))) { seenP.add(p10(l.phone)); phones.push(l.phone); }
    const em = l.email?.trim().toLowerCase();
    if (l.email && em && !seenE.has(em)) { seenE.add(em); emails.push(l.email.trim()); }
  }
  const spelled = lines
    .map((l) => [l.name, l.role && `(${l.role})`, l.phone, l.ext && `x${l.ext}`, l.email].filter(Boolean).join(" "))
    .filter(Boolean);
  return {
    phone1: phones[0] ?? null,
    phone2: phones[1] ?? null,
    email1: emails[0] ?? null,
    email2: emails[1] ?? null,
    notes: spelled.length ? `Contacts at handoff: ${spelled.join(" · ")}` : null,
  };
}
